import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  MessageSquare,
  Pause,
  XCircle,
} from 'lucide-react';

import type { LucideIcon } from 'lucide-react';

export type AgentStatus = 'working' | 'awaiting-input' | 'complete' | 'paused' | 'on-hold' | 'cancelled' | 'not-started' | 'idle';

export interface ConversationListItemProps {
  /** Unique conversation identifier */
  id: string;
  /** Conversation display title */
  title: string;
  /** Preview text (last message snippet) */
  preview?: string;
  /** Timestamp string (e.g., "2m ago", "Yesterday") */
  timestamp?: string;
  /** Whether this conversation has unread messages */
  unread?: boolean;
  /** Whether this conversation is currently selected/active */
  active?: boolean;
  /** Derived agent status for the status indicator */
  agentStatus?: AgentStatus;
  /** Task stage label (only shown when task is linked) */
  taskStage?: 'not-started' | 'awaiting-input' | 'working' | 'complete';
  /** Task overlay state */
  taskOverlay?: 'paused' | 'on-hold' | 'cancelled' | null;
  /** Whether this conversation is linked to a task */
  taskLinked?: boolean;
  /** Linked task ID (enables clickable chip) */
  taskId?: string;
  /** Linked task title (displayed in the chip) */
  taskTitle?: string;
  /** Click handler */
  onClick?: (id: string) => void;
  /** Handler to navigate to the linked task */
  onTaskNavigate?: (taskId: string) => void;
  /** Additional CSS classes */
  className?: string;
}

interface StatusConfig {
  icon: LucideIcon;
  ring: string;
  bg: string;
  text: string;
  animate: string;
  glow: string;
}

const STATUS_CONFIG: Record<AgentStatus, StatusConfig> = {
  working: {
    icon: Loader2,
    ring: 'ring-1 ring-sd-info/25',
    bg: 'bg-gradient-to-br from-sd-info/20 to-sd-info/10',
    text: 'text-sd-info',
    animate: 'animate-[spin_3s_linear_infinite]',
    glow: 'shadow-[0_0_8px_-2px_var(--sd-info)]',
  },
  'awaiting-input': {
    icon: AlertCircle,
    ring: 'ring-1 ring-sd-warning/30',
    bg: 'bg-gradient-to-br from-sd-warning/20 to-sd-warning/8',
    text: 'text-sd-warning',
    animate: '',
    glow: 'shadow-[0_0_6px_-2px_var(--sd-warning)]',
  },
  complete: {
    icon: CheckCircle2,
    ring: 'ring-1 ring-sd-success/20',
    bg: 'bg-gradient-to-br from-sd-success/15 to-sd-success/5',
    text: 'text-sd-success/80',
    animate: '',
    glow: '',
  },
  paused: {
    icon: Pause,
    ring: 'ring-1 ring-sd-warning/15',
    bg: 'bg-sd-warning/8',
    text: 'text-sd-warning/60',
    animate: '',
    glow: '',
  },
  'on-hold': {
    icon: Clock,
    ring: 'ring-1 ring-sd-warning/15',
    bg: 'bg-sd-warning/8',
    text: 'text-sd-warning/60',
    animate: '',
    glow: '',
  },
  cancelled: {
    icon: XCircle,
    ring: 'ring-1 ring-sd-error/15',
    bg: 'bg-sd-error/8',
    text: 'text-sd-error/50',
    animate: '',
    glow: '',
  },
  'not-started': {
    icon: Circle,
    ring: 'ring-1 ring-sd-border/30',
    bg: 'bg-sd-surface-elevated/50',
    text: 'text-sd-text-muted/50',
    animate: '',
    glow: '',
  },
  idle: {
    icon: MessageSquare,
    ring: 'ring-1 ring-sd-primary/15',
    bg: 'bg-sd-primary-subtle/60',
    text: 'text-sd-primary/70',
    animate: '',
    glow: '',
  },
};

const STAGE_LABELS: Record<string, string> = {
  'not-started': 'Queued',
  'awaiting-input': 'Needs You',
  working: 'Working',
  complete: 'Done',
};

const STAGE_TEXT_COLOR: Record<string, string> = {
  'not-started': 'text-sd-text-muted/60',
  'awaiting-input': 'text-sd-warning/70',
  working: 'text-sd-info/70',
  complete: 'text-sd-success/70',
};

const OVERLAY_LABELS: Record<string, string> = {
  paused: 'Paused',
  'on-hold': 'On Hold',
  cancelled: 'Cancelled',
};

export function ConversationListItem({
  id,
  title,
  preview,
  timestamp,
  unread,
  active,
  agentStatus = 'idle',
  taskStage,
  taskOverlay,
  taskLinked,
  taskId,
  taskTitle,
  onClick,
  onTaskNavigate,
  className = '',
}: ConversationListItemProps) {
  const cfg = STATUS_CONFIG[agentStatus];
  const hasOverlay = taskOverlay === 'paused' || taskOverlay === 'on-hold' || taskOverlay === 'cancelled';

  const bgClass = active
    ? 'bg-sd-primary-subtle shadow-[inset_0_0_0_1px_rgba(108,122,136,0.08)]'
    : 'hover:bg-sd-surface-elevated';

  const overlayClasses = hasOverlay
    ? 'opacity-50 grayscale-[30%] saturate-[70%] hover:opacity-70 hover:grayscale-0 hover:saturate-100'
    : '';

  return (
    <li
      role="option"
      aria-selected={active ?? false}
      aria-label={title}
      tabIndex={0}
      className={`relative flex gap-sd-2 px-sd-3 py-sd-2 cursor-pointer transition-[opacity,filter,background-color,box-shadow] duration-300 ease-out motion-reduce:transition-none border-b border-sd-border/20 ${bgClass} ${overlayClasses} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 ${className}`.trim()}
      onClick={() => onClick?.(id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(id);
        }
      }}
    >
      {/* Accent bar: unread (amber) takes priority over active (primary) */}
      {unread ? (
        <div data-testid="unread-bar" className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full bg-sd-warning" />
      ) : active ? (
        <div data-testid="active-bar" className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full bg-sd-primary" />
      ) : null}

      {/* Cancelled red wash overlay */}
      {taskOverlay === 'cancelled' && (
        <div className="absolute inset-0 bg-sd-error/[0.03] pointer-events-none rounded-inherit" />
      )}

      {/* Status indicator — 32x32 signal light, hidden when idle (no linked task) */}
      {agentStatus !== 'idle' && (
        <div
          data-testid="status-indicator"
          className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 self-center ${cfg.ring} ${cfg.bg} ${cfg.glow} transition-all duration-500 ease-out`}
        >
          <cfg.icon
            size={14}
            className={`${cfg.text} ${cfg.animate} motion-reduce:animate-none`}
            strokeWidth={2}
            aria-hidden="true"
          />
        </div>
      )}

      {/* Content area — vertically stacked rows */}
      <div className="flex-1 min-w-0 flex flex-col gap-px">
        {/* Row 1: Title + unread dot + timestamp */}
        <div className="flex items-center gap-sd-1">
          <span className={`text-sd-sm ${unread ? 'font-semibold' : 'font-medium'} text-sd-text truncate`}>{title}</span>
          {unread && (
            <span
              data-testid="unread-dot"
              className="w-1.5 h-1.5 rounded-full bg-sd-warning shrink-0 shadow-[0_0_4px_0px_var(--sd-warning)]"
              aria-hidden="true"
            />
          )}
          {timestamp && <span className="text-sd-xs text-sd-text-muted shrink-0 ml-auto tabular-nums">{timestamp}</span>}
        </div>

        {/* Row 2: Stage + overlay labels (conditional on task link) */}
        {taskLinked && taskStage && (
          <div data-testid="stage-row" className="flex items-center gap-1.5">
            <span className={`text-[10px] font-medium tracking-wider uppercase ${STAGE_TEXT_COLOR[taskStage] ?? 'text-sd-text-muted/60'}`}>
              {STAGE_LABELS[taskStage] ?? taskStage}
            </span>
            {taskOverlay && OVERLAY_LABELS[taskOverlay] && (
              <span className="text-[10px] font-medium tracking-wider uppercase text-sd-warning/60 border border-dashed border-sd-warning/30 rounded-sm px-1 py-px leading-none">
                {OVERLAY_LABELS[taskOverlay]}
              </span>
            )}
          </div>
        )}

        {/* Row 3: Preview — 1-line clamp */}
        {preview && (
          <p className="text-sd-xs text-sd-text-muted/70 line-clamp-1 leading-snug">{preview}</p>
        )}

        {/* Row 4: Task chip (existing) */}
        {taskLinked && taskId && taskTitle && onTaskNavigate && (
          <div className="flex items-center gap-sd-1 mt-px">
            <button
              type="button"
              aria-label={`Go to task: ${taskTitle}`}
              className="inline-flex items-center gap-0.5 max-w-[160px] bg-sd-info/15 text-sd-info text-[10px] leading-none px-1 py-0.5 font-semibold rounded-sd-sm cursor-pointer hover:bg-sd-info/25 transition-colors duration-150 shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onTaskNavigate(taskId);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                }
              }}
            >
              <ArrowUpRight size={10} className="shrink-0" />
              <span className="truncate">{taskTitle}</span>
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
